import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Skeleton,
  useSidebar,
} from "@base/ui";
import { Building2, ChevronsUpDown, Plus } from "lucide-react";
import { useState } from "react";
import { useClienteSelecionado } from "../application/clientes.contexto";
import { useCriarCliente } from "../application/use-clientes";
import { iniciaisDoCliente, ROTULO_DO_PAPEL_NO_CLIENTE } from "../domain/papeis-no-cliente";
import { FormularioDeCliente } from "./formulario-de-cliente";

/**
 * Seletor de cliente no topo da barra lateral: o `TeamSwitcher` do bloco
 * `sidebar-07`, com as clinicas que a pessoa pode acessar. A equipe Monte
 * tambem cadastra cliente por aqui.
 *
 * Sem o atalho ⌘1..⌘9 do template: no navegador ele troca de aba.
 */
export function SeletorDeCliente({ podeAdicionar }: { readonly podeAdicionar: boolean }) {
  const { isMobile } = useSidebar();
  const { clientes, selecionado, carregando, selecionar } = useClienteSelecionado();
  const [criando, setCriando] = useState(false);
  const criar = useCriarCliente();

  if (carregando) {
    return (
      <div className="flex items-center gap-2 p-2" aria-hidden="true">
        <Skeleton className="size-8 rounded-lg bg-sidebar-accent" />
        <Skeleton className="h-4 flex-1 bg-sidebar-accent" />
      </div>
    );
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                aria-label="Trocar de cliente"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <span className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-xs font-bold text-sidebar-primary-foreground">
                  {selecionado ? (
                    iniciaisDoCliente(selecionado.nome)
                  ) : (
                    <Building2 className="size-4" aria-hidden="true" />
                  )}
                </span>
                <span className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {selecionado?.nome ?? "Nenhum cliente"}
                  </span>
                  <span className="truncate text-xs opacity-70">
                    {selecionado === null
                      ? podeAdicionar
                        ? "Cadastre o primeiro"
                        : "Sem acesso ainda"
                      : selecionado.meuPapel
                        ? ROTULO_DO_PAPEL_NO_CLIENTE[selecionado.meuPapel]
                        : selecionado.ativo
                          ? "Equipe Monte"
                          : "Desativado"}
                  </span>
                </span>
                <ChevronsUpDown className="ml-auto" aria-hidden="true" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-xl"
              align="start"
              side={isMobile ? "bottom" : "right"}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">Clientes</DropdownMenuLabel>
              {clientes.length === 0 ? (
                <p className="px-2 py-1.5 text-sm text-muted-foreground">
                  {podeAdicionar
                    ? "Nenhum cliente cadastrado."
                    : "Você ainda não tem acesso a nenhum cliente."}
                </p>
              ) : null}
              {clientes.map((cliente) => (
                <DropdownMenuItem
                  key={cliente.id}
                  onSelect={() => selecionar(cliente.id)}
                  className="gap-2 p-2"
                  aria-current={cliente.id === selecionado?.id ? "true" : undefined}
                >
                  <span className="flex size-6 items-center justify-center rounded-md border text-[10px] font-bold">
                    {iniciaisDoCliente(cliente.nome)}
                  </span>
                  <span className={cliente.ativo ? "truncate" : "truncate text-muted-foreground line-through"}>
                    {cliente.nome}
                  </span>
                </DropdownMenuItem>
              ))}
              {podeAdicionar ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2 p-2" onSelect={() => setCriando(true)}>
                    <span className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                      <Plus className="size-4" aria-hidden="true" />
                    </span>
                    <span className="font-medium text-muted-foreground">Adicionar cliente</span>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <Dialog open={criando} onOpenChange={setCriando}>
        <DialogContent tamanho="formulario">
          <DialogHeader rotulo="Gestão">
            <DialogTitle>Novo cliente</DialogTitle>
            <DialogDescription>
              Depois de cadastrar, dê acesso às pessoas da clínica em Gestão › Acessos.
            </DialogDescription>
          </DialogHeader>
          {criando ? (
            <FormularioDeCliente
              rotuloDoBotao="Cadastrar cliente"
              enviando={criar.isPending}
              erro={criar.error}
              aoEnviar={(nome) =>
                criar.mutate(nome, {
                  onSuccess: (cliente) => {
                    selecionar(cliente.id);
                    setCriando(false);
                    criar.reset();
                  },
                })
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
